import React, { useState, useEffect, useRef } from "react";
import Swal from "sweetalert2";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBell,
  faUser,
  faSignOutAlt,
  faBars,
  faChartLine,
  faFileUpload,
  faListAlt,
  faClipboardCheck,
  faClipboardList,
  faPlus,
  faHospital,
  faHospitalUser,
  faUserMd,
  faUserPlus,
  faCalendarCheck,
  faFileMedical,
  faNotesMedical,
  faMoneyCheckAlt,
  faBuilding,
  faTasks,
  faTools,
  faTable,
  faSearch,
  faUsers,
  faEnvelope,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { useSearch } from "../contexts/SearchContext"; // Adjust path if needed
import messagesApi from "../services/messagesApi";

import { Sparkles } from "lucide-react";

const NavbarWithWebSocket = ({
  isSidebarOpen,
  sidebarOffset,
  onToggleSidebar,
}) => {
  const navigate = useNavigate();
  const [showNotifications, setShowNotifications] = useState(false);
  const [messagePendingCount, setMessagePendingCount] = useState(0);
  const [approvalNotifications, setApprovalNotifications] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("messages.approvalNotifications") || "[]");
      return Array.isArray(stored) ? stored : [];
    } catch (error) {
      return [];
    }
  });
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const notificationsRef = useRef();
  const userMenuRef = useRef();
  const searchRef = useRef();
  const { setIsSearchOpen } = useSearch();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        notificationsRef.current &&
        !notificationsRef.current.contains(event.target)
      ) {
        setShowNotifications(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
        setSelectedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setSelectedIndex(-1);
  }, [searchTerm]);

  const handleToggleClick = () => onToggleSidebar();
  const handleBellClick = () => {
    setShowNotifications((prev) => !prev);
  };
  const handleUserIconClick = () => setShowUserMenu((prev) => !prev);
  const handleLogout = () => {
    Swal.fire({
      title: "Logout?",
      text: "Are you sure you want to logout?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#4f46e5",
      cancelButtonColor: "#ef4444",
      confirmButtonText: "Yes, logout",
      cancelButtonText: "Cancel",
      customClass: {
        popup: "rounded-xl shadow-lg",
        confirmButton:
          "bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-md",
        cancelButton:
          "bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-4 rounded-md",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        localStorage.clear();
        window.location.reload();
      }
    });
  };
  const handleChangePasswordClick = () => {
    navigate("/change-password");
  };

  useEffect(() => {
    let cancelled = false;
    const snapshotKey = "messages.approvalSnapshot";
    const notificationsKey = "messages.approvalNotifications";
    const statusLabels = {
      1: "Approved",
      3: "Returned",
      4: "Disapproved",
    };

    const readSnapshot = () => {
      try {
        const parsed = JSON.parse(localStorage.getItem(snapshotKey) || "{}");
        return parsed && typeof parsed === "object" ? parsed : {};
      } catch (error) {
        return {};
      }
    };

    const saveNotifications = (items) => {
      localStorage.setItem(notificationsKey, JSON.stringify(items.slice(0, 50)));
    };

    const pollMessages = async () => {
      try {
        const countResponse = await messagesApi.getPendingCount();
        if (!cancelled) {
          setMessagePendingCount(Number(countResponse.data?.data?.count || 0));
        }
      } catch (error) {
        if (!cancelled) setMessagePendingCount(0);
      }

      try {
        const previousSnapshot = readSnapshot();
        const trackedIds = Object.keys(previousSnapshot)
          .map((value) => Number(value))
          .filter((value) => Number.isFinite(value) && value > 0);
        const response = await messagesApi.getApprovalStatusNotifications(trackedIds);
        const rows = response.data?.data || [];
        const nextSnapshot = {};
        const freshNotifications = [];

        rows.forEach((row) => {
          const id = Number(row.tbl_id);
          const status = Number(row.status);
          if (!id) return;
          nextSnapshot[id] = status;

          const previousStatus = previousSnapshot[id];
          if (
            previousStatus !== undefined &&
            Number(previousStatus) !== status &&
            statusLabels[status]
          ) {
            freshNotifications.push({
              key: `${id}-${status}`,
              tbl_id: id,
              trans_no: row.trans_no || "-",
              status,
              label: statusLabels[status],
              generated_at: response.data?.generated_at || new Date().toISOString(),
            });
          }
        });

        localStorage.setItem(snapshotKey, JSON.stringify(nextSnapshot));

        if (!cancelled && freshNotifications.length > 0) {
          setApprovalNotifications((current) => {
            const seen = new Set(current.map((item) => item.key));
            const merged = [
              ...freshNotifications.filter((item) => !seen.has(item.key)),
              ...current,
            ].slice(0, 50);
            saveNotifications(merged);
            return merged;
          });
        }
      } catch (error) {
        // Notifications are best-effort; forbidden users simply skip this poll.
      }
    };

    pollMessages();
    const interval = window.setInterval(pollMessages, 10000);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  const openMessagesFromBadge = () => {
    localStorage.setItem("messages.activeTab", "pending");
    window.dispatchEvent(new CustomEvent("messages:set-active-tab", { detail: "pending" }));
    navigate("/messages");
  };

  const openApprovalNotification = (item) => {
    localStorage.setItem("messages.activeTab", "approval_requests");
    localStorage.setItem("messages.approvalDetail", JSON.stringify(item));
    window.dispatchEvent(new CustomEvent("messages:set-active-tab", { detail: "approval_requests" }));
    setShowNotifications(false);
    navigate("/messages");
  };

  const clearApprovalNotifications = () => {
    localStorage.setItem("messages.approvalNotifications", "[]");
    setApprovalNotifications([]);
  };

  const resolvedSidebarOffset =
    typeof sidebarOffset === "number"
      ? sidebarOffset
      : isSidebarOpen
      ? 288
      : 80;

  // Define searchable items based on routes and modules (flexible: you can add more items here later)
  const allItems = [
    {
      title: "Dashboard",
      path: "/dashboard",
      icon: faChartLine,
      description: "Overview of the system",
    },
    {
      title: "Messages",
      path: "/messages",
      icon: faEnvelope,
      description: "SMS queues, approval requests, and customer feedbacks",
    },
    {
      title: "Received/Scanned Date Uploading",
      path: "/claims/receiving",
      icon: faFileUpload,
      description: "Upload dates for claims batches",
    },
    {
      title: "Claims Batch Registration",
      path: "/claims/batches",
      icon: faClipboardList,
      description: "Register claims batches",
    },
    {
      title: "Claims Stale Checker",
      path: "/claims/stale-checker",
      icon: faSearch,
      description: "Check if LOA is stale or still valid",
    },
    {
      title: "Physician Not Yet Enrolled",
      path: "/claims/physician-not-yet-enrolled",
      icon: faUserMd,
      description: "Review pending physicians from transaction generation",
    },
    {
      title: "Add Claims Batch",
      path: "/claims/batches/add",
      icon: faPlus,
      description: "Create a new claims batch",
    },
    {
      title: "Providers",
      path: "/medical/provider",
      icon: faHospital,
      description: "Manage medical providers",
    },
    {
      title: "Provider Submission of Claims (Contract) Days Settings",
      path: "/medical/provider-claims-submission-settings",
      icon: faHospital,
      description: "Update provider submission of claims (contract) days",
    },
    {
      title: "Add New Provider",
      path: "/medical/add-new-provider",
      icon: faHospitalUser,
      description: "Enroll a new provider",
    },
    {
      title: "Physician",
      path: "/physician",
      icon: faUserMd,
      description: "Manage physicians",
    },
    {
      title: "Add Physician",
      path: "/physician/add",
      icon: faUserPlus,
      description: "Enroll a new physician",
    },
    {
      title: "APE Scheduler",
      path: "/ape-scheduler",
      icon: faCalendarCheck,
      description: "Schedule APE",
    },
    {
      title: "CPT",
      path: "/cpt",
      icon: faFileMedical,
      description: "Current Procedural Terminology",
    },
    {
      title: "Add New CPT",
      path: "/cpt/add-new-cpt",
      icon: faPlus,
      description: "Add a new CPT code",
    },
    {
      title: "CPT Batch Upload",
      path: "/cpt-batch-upload",
      icon: faFileUpload,
      description: "Upload CPT batch CSV",
    },
    {
      title: "CPT Batch Upload History",
      path: "/history-cpt-batch-upload",
      icon: faListAlt,
      description: "View CPT batch upload history",
    },
    {
      title: "CPT Batch Processed Data",
      path: "/processed-cpt-batch-upload",
      icon: faClipboardCheck,
      description: "View processed CPT batch rows",
    },
    {
      title: "ICD",
      path: "/icd",
      icon: faNotesMedical,
      description: "International Classification of Diseases",
    },
    {
      title: "Add ICD",
      path: "/icd/add",
      icon: faPlus,
      description: "Add a new ICD code",
    },
    {
      title: "RVU",
      path: "/rvu",
      icon: faMoneyCheckAlt,
      description: "Relative Value Units",
    },
    {
      title: "Add RVU",
      path: "/rvu/add",
      icon: faPlus,
      description: "Add a new RVU",
    },
    {
      title: "Corporate",
      path: "/corporate",
      icon: faBuilding,
      description: "Manage corporates",
    },
    {
      title: "Add Corporate",
      path: "/corporate/add",
      icon: faPlus,
      description: "Enroll a new corporate",
    },
    // {
    //   title: "Corporate List",
    //   path: "/corporate/list",
    //   icon: faBuilding,
    //   description: "List of corporates",
    // },
    // {
    //   title: "Corporate Status List",
    //   path: "/corporate/status-list",
    //   icon: faBuilding,
    //   description: "Corporate statuses",
    // },
    {
      title: "Plan & Coverage Setup",
      path: "/plan-and-coverage-benefits-setup",
      icon: faTasks,
      description: "Setup plans and coverages",
    },
    {
      title: "Auxiliary Tools",
      path: "/auxiliary-tools",
      icon: faTools,
      description: "Additional utilities",
    },
    {
      title: "Reference Coverage Master",
      path: "/ref-master-table-list/ref-coverage-master",
      icon: faTable,
      description: "Master table for coverage",
    },
    {
      title: "Reference Benefit Master",
      path: "/ref-master-table-list/ref-benefit-master",
      icon: faTable,
      description: "Master table for benefits",
    },
    {
      title: "Members",
      path: "/members",
      icon: faUsers,
      description: "Member modules",
    },
    // Add more items here as needed for flexibility (e.g., new modules/pages)
  ];

  const filteredItems = allItems.filter((item) =>
    item.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowSearchResults(!!value);
  };

  const handleKeyDown = (e) => {
    if (!showSearchResults || filteredItems.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) =>
        prev < filteredItems.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) =>
        prev > 0 ? prev - 1 : filteredItems.length - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (selectedIndex >= 0 && selectedIndex < filteredItems.length) {
        const item = filteredItems[selectedIndex];
        navigate(item.path);
        setSearchTerm("");
        setShowSearchResults(false);
        setSelectedIndex(-1);
      }
    } else if (e.key === "Escape") {
      setShowSearchResults(false);
      setSelectedIndex(-1);
    }
  };

  return (
    <div
      className="bg-white/80 backdrop-blur-md shadow-[0_1px_3px_rgba(0,0,0,0.05)] border-b border-gray-200/60 h-16 flex items-center px-5 fixed top-0 right-0 z-50 transition-all duration-300"
      style={{ left: `${resolvedSidebarOffset}px` }}
    >
      {/* Toggle Button */}
      <button
        className="text-gray-500 hover:text-gray-700 transition-colors p-2 rounded-lg hover:bg-gray-100"
        onClick={handleToggleClick}
      >
        <FontAwesomeIcon icon={faBars} className="text-lg" />
      </button>

      {/* Title */}
      {/* <h1 className="text-xl font-bold text-gray-900 tracking-tight ml-4">AllAccess V2</h1> */}

      {/* Search Field */}
      <div className="flex-1 ml-4 relative" ref={searchRef}>
        <div className="relative">
          <FontAwesomeIcon
            icon={faSearch}
            className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"
          />
          <input
            type="text"
            placeholder="Search modules and pages... (Ctrl + G)"
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 text-sm text-gray-700 bg-gray-50/50 placeholder-gray-400 transition-all duration-200"
            value={searchTerm}
            onChange={handleSearchChange}
            onFocus={() => setShowSearchResults(true)}
            onKeyDown={handleKeyDown}
          />
        </div>
        {showSearchResults && searchTerm && (
          <div className="absolute left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-xl max-h-80 overflow-y-auto z-50">
            {filteredItems.length > 0 ? (
              filteredItems.map((item, index) => (
                <button
                  key={index}
                  className={`w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-indigo-100 hover:text-indigo-800 transition-colors border-b last:border-b-0 ${
                    index === selectedIndex
                      ? "bg-indigo-200 text-indigo-900"
                      : ""
                  }`}
                  onClick={() => {
                    navigate(item.path);
                    setSearchTerm("");
                    setShowSearchResults(false);
                    setSelectedIndex(-1);
                  }}
                >
                  <FontAwesomeIcon
                    icon={item.icon}
                    className="mr-3 text-indigo-600"
                  />
                  <span className="font-medium">{item.title}</span>
                  {item.description && (
                    <p className="text-xs text-gray-500 mt-1">
                      {item.description}
                    </p>
                  )}
                </button>
              ))
            ) : (
              <div className="p-4 text-gray-500 text-sm text-center">
                No results found
              </div>
            )}
          </div>
        )}
      </div>

      {/* Icons Section */}
      <div className="flex items-center gap-3 ml-4">
        {/* Modal Search Button */}
        <button
          className="text-gray-500 hover:text-indigo-600 transition-colors p-2 rounded-lg hover:bg-indigo-50"
          onClick={() => setIsSearchOpen(true)}
          title="Search Modules (Ctrl+G)"
        >
          <FontAwesomeIcon icon={faSearch} className="text-lg" />
        </button>

        {/* Ultra Modern AI Assistant Button */}
        <motion.button
          onClick={() => navigate("/ai-assistant")}
          className="relative flex items-center gap-3 px-6 py-3 rounded-2xl bg-gradient-to-r from-pink-500 via-purple-600 to-cyan-500 text-white font-semibold shadow-2xl transform transition-all duration-500 hover:shadow-3xl focus:outline-none focus:ring-4 focus:ring-pink-400/50 overflow-hidden group"
          whileHover={{
            scale: 1.08,
            y: -2,
          }}
          whileTap={{
            scale: 0.95,
            y: 0,
          }}
          animate={{
            backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
          }}
          transition={{
            backgroundPosition: {
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut",
            },
          }}
          style={{
            backgroundSize: "200% 200%",
          }}
        >
          {/* Animated Background Particles */}
          <div className="absolute inset-0 overflow-hidden">
            {/* Floating AI Particles */}
            <motion.div
              className="absolute w-2 h-2 bg-white rounded-full opacity-30"
              animate={{
                x: [0, 120, 0],
                y: [10, -15, 10],
                scale: [1, 1.5, 1],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                delay: 0,
              }}
            />
            <motion.div
              className="absolute w-1.5 h-1.5 bg-cyan-300 rounded-full opacity-40"
              animate={{
                x: [100, 0, 100],
                y: [-10, 20, -10],
                scale: [1, 2, 1],
              }}
              transition={{
                duration: 3.5,
                repeat: Infinity,
                delay: 1,
              }}
            />
            <motion.div
              className="absolute w-1 h-1 bg-pink-300 rounded-full opacity-50"
              animate={{
                x: [50, 80, 50],
                y: [30, -10, 30],
                scale: [1, 1.8, 1],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                delay: 2,
              }}
            />
          </div>

          {/* Pulsing Glow Effect */}
          <motion.div
            className="absolute inset-0 rounded-2xl bg-gradient-to-r from-pink-400/30 via-purple-500/30 to-cyan-400/30"
            animate={{
              opacity: [0.3, 0.7, 0.3],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />

          {/* Scanning Wave Effect */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
            animate={{ x: [-200, 200] }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 0.5,
            }}
          />

          {/* Icon Container with Advanced Animation */}
          <div className="relative z-10">
            <motion.div
              className="relative"
              animate={{
                rotate: [0, 5, -5, 0],
                scale: [1, 1.1, 1],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <Sparkles className="h-6 w-6 text-white" />

              {/* Orbiting Particles around Sparkles */}
              <motion.div
                className="absolute -top-1 -right-1 w-1.5 h-1.5 bg-yellow-300 rounded-full"
                animate={{
                  scale: [1, 1.8, 1],
                  opacity: [0.7, 1, 0.7],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
              <motion.div
                className="absolute -bottom-1 -left-1 w-1 h-1 bg-cyan-300 rounded-full"
                animate={{
                  scale: [1, 2, 1],
                  opacity: [0.5, 1, 0.5],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1,
                }}
              />
            </motion.div>
          </div>

          {/* Text with Glitch Effect */}
          <motion.span
            className="relative z-10 text-lg font-bold tracking-wide"
            animate={{
              textShadow: [
                "0 0 0px rgba(255,255,255,0)",
                "0 0 10px rgba(255,255,255,0.8)",
                "0 0 0px rgba(255,255,255,0)",
              ],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            AI Assistant
          </motion.span>

          {/* Hover Sparkle Burst */}
          <motion.div
            className="absolute inset-0 opacity-0 group-hover:opacity-100 pointer-events-none"
            initial={false}
          >
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 bg-white rounded-full"
                initial={{
                  x: "50%",
                  y: "50%",
                  scale: 0,
                  opacity: 1,
                }}
                whileHover={{
                  x: Math.cos((i * 60 * Math.PI) / 180) * 40 + "50%",
                  y: Math.sin((i * 60 * Math.PI) / 180) * 40 + "50%",
                  scale: [0, 2, 0],
                  opacity: [0, 1, 0],
                }}
                transition={{
                  duration: 0.8,
                  ease: "easeOut",
                }}
              />
            ))}
          </motion.div>

          {/* Connection Lines Animation */}
          <motion.div
            className="absolute inset-0 opacity-0 group-hover:opacity-100"
            initial={false}
          >
            <motion.div
              className="absolute top-0 left-1/4 w-0.5 h-1/2 bg-gradient-to-b from-transparent to-cyan-300"
              whileHover={{
                height: "100%",
                opacity: [0, 1, 0],
              }}
              transition={{ duration: 0.6 }}
            />
            <motion.div
              className="absolute top-0 right-1/4 w-0.5 h-1/2 bg-gradient-to-b from-transparent to-pink-300"
              whileHover={{
                height: "100%",
                opacity: [0, 1, 0],
              }}
              transition={{ duration: 0.6, delay: 0.2 }}
            />
          </motion.div>
        </motion.button>

        {/* Messages Pending Badge */}
        <button
          className="relative text-gray-500 hover:text-blue-600 transition-colors p-2 rounded-lg hover:bg-blue-50"
          onClick={openMessagesFromBadge}
          title="Messages"
        >
          <FontAwesomeIcon icon={faEnvelope} className="text-lg" />
          {messagePendingCount > 0 && (
            <span className="absolute -right-1 -top-1 min-w-5 rounded-full bg-red-600 px-1.5 py-0.5 text-center text-[10px] font-bold leading-none text-white">
              {messagePendingCount > 99 ? "99+" : messagePendingCount}
            </span>
          )}
        </button>

        {/* Notification Bell */}
        <div className="relative" ref={notificationsRef}>
          <button
            className="relative text-gray-500 hover:text-purple-600 transition-colors p-2 rounded-lg hover:bg-purple-50"
            onClick={handleBellClick}
          >
            <FontAwesomeIcon icon={faBell} className="text-lg" />
            {approvalNotifications.length > 0 && (
              <span className="absolute -right-1 -top-1 min-w-5 rounded-full bg-red-600 px-1.5 py-0.5 text-center text-[10px] font-bold leading-none text-white">
                {approvalNotifications.length > 99 ? "99+" : approvalNotifications.length}
              </span>
            )}
          </button>
          {showNotifications && (
            <div className="absolute right-0 mt-3 w-96 max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-xl shadow-xl max-h-80 overflow-y-auto">
              <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
                <div className="text-sm font-semibold text-gray-800">
                  Approval Requests
                </div>
                {approvalNotifications.length > 0 && (
                  <button
                    type="button"
                    className="text-xs font-semibold text-indigo-600 hover:text-indigo-800"
                    onClick={clearApprovalNotifications}
                  >
                    Clear
                  </button>
                )}
              </div>
              {approvalNotifications.length > 0 ? (
                approvalNotifications.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className="w-full border-b border-gray-100 px-4 py-3 text-left text-sm hover:bg-indigo-50"
                    onClick={() => openApprovalNotification(item)}
                  >
                    <div className="font-semibold text-gray-800">
                      TRN# {item.trans_no || "-"} {item.label}
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      {item.generated_at}
                    </div>
                  </button>
                ))
              ) : (
                <div className="p-4 text-gray-500 text-sm text-center">
                  No recent notifications
                </div>
              )}
            </div>
          )}
        </div>
        {/* User Icon / Menu */}
        <div className="relative" ref={userMenuRef}>
          <button
            className="text-gray-500 hover:text-gray-700 transition-colors p-2 rounded-lg hover:bg-gray-100"
            onClick={handleUserIconClick}
          >
            <FontAwesomeIcon icon={faUser} className="text-lg" />
          </button>
          {showUserMenu && (
            <div className="absolute right-0 mt-3 w-52 bg-white border border-gray-200 rounded-xl shadow-xl">
              <button
                className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-indigo-100 hover:text-indigo-800 rounded-t-md transition-colors"
                onClick={() => navigate("/profile")}
              >
                Profile
              </button>
              <button
                className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-indigo-100 hover:text-indigo-800 transition-colors"
                onClick={handleChangePasswordClick}
              >
                Change Password
              </button>
              <button
                className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-indigo-100 hover:text-indigo-800 rounded-b-md transition-colors"
                onClick={handleLogout}
              >
                <FontAwesomeIcon
                  icon={faSignOutAlt}
                  className="mr-2 text-red-600"
                />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NavbarWithWebSocket;
